import React, { useState, useRef, useCallback } from 'react';
import Header from '../components/Layout/Header';
import { API_CONFIG } from '../config/api';
import {
  Upload, ScanText, Copy, Check, Loader2, Image, FileText,
  Code, Download, X, RotateCcw, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';

type OutputFormat = 'plain' | 'markdown' | 'structured';
type Strategy = '' | 'TESSERACT' | 'VISION' | 'HYBRID';

interface OcrAnalysis {
  documentType: string;
  isHandwritten: boolean;
  complexity: string;
  confidence: number;
}

interface OcrResult {
  text: string;
  model: string;
  tokens: { input: number; output: number; total: number };
  outputFormat: string;
  strategy: string;
  analysis: OcrAnalysis;
  processingTimeMs: number;
  timestamp: string;
}

interface DocInfo {
  type: string;
  subType?: string;
  title?: string;
  confidence: number;
  jurisdiction?: string;
  parties?: string[];
  dates?: string[];
  amounts?: string[];
  sections?: string[];
  summary?: string;
}

const OcrWidgetPage: React.FC = () => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageMime, setImageMime] = useState<string>('image/png');
  const [fileName, setFileName] = useState<string>('');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('plain');
  const [language, setLanguage] = useState<string>('');
  const [forceStrategy, setForceStrategy] = useState<Strategy>('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OcrResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [showWidgetCode, setShowWidgetCode] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Document Intelligence state
  const [docInfo, setDocInfo] = useState<DocInfo | null>(null);
  const [legalAnalysis, setLegalAnalysis] = useState<string>('');
  const [analyzing, setAnalyzing] = useState(false);

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image must be under 20MB');
      return;
    }

    setFileName(file.name);
    setImageMime(file.type);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setImagePreview(dataUrl);
      // Strip the data:image/...;base64, prefix
      const base64 = dataUrl.split(',')[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) handleFile(file);
        break;
      }
    }
  }, [handleFile]);

  const runOcr = async () => {
    if (!imageBase64) return;
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API_CONFIG.core}/api/ocr/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageBase64,
          mimeType: imageMime,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setResult(data);
        toast.success('Text extracted successfully');
      } else {
        toast.error(data.error || 'OCR failed');
      }
    } catch (err: any) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Run Document Intelligence (streaming legal analysis)
  const runAnalysis = async () => {
    if (!result?.text) return;
    setAnalyzing(true);
    setLegalAnalysis('');
    setDocInfo(null);

    try {
      const response = await fetch(`${API_CONFIG.core}/api/ocr/analyze/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: result.text }),
      });

      if (!response.ok || !response.body) {
        toast.error('Analysis failed');
        setAnalyzing(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullAnalysis = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (currentEvent === 'docInfo') {
                setDocInfo(data);
              } else if (currentEvent === 'chunk' && data.text) {
                fullAnalysis += data.text;
                setLegalAnalysis(fullAnalysis);
              } else if (currentEvent === 'error') {
                toast.error(data.error || 'Analysis error');
              }
            } catch (e) {}
          }
        }
      }

      toast.success('Legal analysis complete');
    } catch (err: any) {
      toast.error(`Analysis error: ${err.message}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadText = () => {
    if (!result) return;
    const ext = outputFormat === 'markdown' ? 'md' : outputFormat === 'structured' ? 'json' : 'txt';
    const blob = new Blob([result.text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ocr-output.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setImagePreview(null);
    setImageBase64(null);
    setFileName('');
    setResult(null);
    setDocInfo(null);
    setLegalAnalysis('');
  };

  const widgetCode = `<!-- JoiLaw OCR Widget -->
<div id="joilaw-ocr-widget"></div>
<script>
(function() {
  const API_URL = 'https://vidhiguruapi.vidhiguru.in/api/ocr/extract';

  const container = document.getElementById('joilaw-ocr-widget');
  container.innerHTML = \`
    <div style="font-family:system-ui,-apple-system,sans-serif;max-width:600px;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#fff;">
      <div style="background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;padding:16px 20px;display:flex;align-items:center;gap:8px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>
        <span style="font-weight:600;">JoiLaw OCR</span>
      </div>
      <div style="padding:20px;">
        <div id="jl-ocr-drop" style="border:2px dashed #d1d5db;border-radius:8px;padding:40px 20px;text-align:center;cursor:pointer;transition:border-color 0.2s;">
          <p style="margin:0;color:#6b7280;font-size:14px;">Drop an image here or click to upload</p>
          <input type="file" id="jl-ocr-input" accept="image/*" style="display:none;" />
        </div>
        <img id="jl-ocr-preview" style="display:none;max-width:100%;border-radius:8px;margin-top:12px;" />
        <div style="margin-top:12px;display:flex;gap:8px;">
          <select id="jl-ocr-format" style="flex:1;padding:8px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;">
            <option value="plain">Plain Text</option>
            <option value="markdown">Markdown</option>
            <option value="structured">Structured JSON</option>
          </select>
          <button id="jl-ocr-btn" style="padding:8px 20px;background:#6366f1;color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;" disabled>
            Extract Text
          </button>
        </div>
        <pre id="jl-ocr-result" style="display:none;margin-top:12px;padding:12px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;font-size:13px;white-space:pre-wrap;max-height:300px;overflow:auto;"></pre>
      </div>
    </div>
  \`;

  const drop = document.getElementById('jl-ocr-drop');
  const input = document.getElementById('jl-ocr-input');
  const preview = document.getElementById('jl-ocr-preview');
  const btn = document.getElementById('jl-ocr-btn');
  const resultEl = document.getElementById('jl-ocr-result');
  const formatSel = document.getElementById('jl-ocr-format');
  let base64Data = null, mimeType = null;

  drop.onclick = () => input.click();
  drop.ondragover = (e) => { e.preventDefault(); drop.style.borderColor = '#6366f1'; };
  drop.ondragleave = () => { drop.style.borderColor = '#d1d5db'; };
  drop.ondrop = (e) => { e.preventDefault(); drop.style.borderColor = '#d1d5db'; loadFile(e.dataTransfer.files[0]); };
  input.onchange = (e) => { if (e.target.files[0]) loadFile(e.target.files[0]); };

  function loadFile(file) {
    if (!file || !file.type.startsWith('image/')) return;
    mimeType = file.type;
    const reader = new FileReader();
    reader.onload = (e) => {
      preview.src = e.target.result;
      preview.style.display = 'block';
      base64Data = e.target.result.split(',')[1];
      btn.disabled = false;
    };
    reader.readAsDataURL(file);
  }

  btn.onclick = async () => {
    if (!base64Data) return;
    btn.disabled = true;
    btn.textContent = 'Extracting...';
    resultEl.style.display = 'none';
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: base64Data, mimeType, outputFormat: formatSel.value })
      });
      const data = await res.json();
      resultEl.textContent = data.success ? data.text : 'Error: ' + (data.error || 'OCR failed');
      resultEl.style.display = 'block';
    } catch (err) {
      resultEl.textContent = 'Error: ' + err.message;
      resultEl.style.display = 'block';
    }
    btn.disabled = false;
    btn.textContent = 'Extract Text';
  };
})();
</script>`;

  return (
    <div className="flex flex-col h-full" onPaste={handlePaste}>
      <Header title="OCR Widget" subtitle="Extract text from images using Gemini Vision AI" />

      <div className="flex-1 overflow-y-auto">
        {/* Hero */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-6">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg">
                <ScanText size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">OCR Text Extraction</h2>
                <p className="text-indigo-200 text-sm">Upload an image, extract text, get embeddable widget code</p>
              </div>
            </div>
            <button
              onClick={() => setShowWidgetCode(!showWidgetCode)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
            >
              <Code size={16} />
              {showWidgetCode ? 'Hide' : 'Get'} Widget Code
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-8 py-6 space-y-6 pb-20">
          {/* Widget Code Panel */}
          {showWidgetCode && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Code size={18} className="text-indigo-600" />
                  <span className="font-semibold text-gray-900">Embeddable Widget Code</span>
                </div>
                <button
                  onClick={() => { copyText(widgetCode); toast.success('Widget code copied'); }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied' : 'Copy Code'}
                </button>
              </div>
              <div className="bg-gray-900 p-4 max-h-80 overflow-auto">
                <pre className="text-xs text-gray-300 font-mono whitespace-pre">{widgetCode}</pre>
              </div>
              <div className="p-4 bg-indigo-50 text-sm text-indigo-700">
                Paste this HTML snippet into any webpage. Make sure the CoreAPI is accessible at the configured URL.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Upload & Settings */}
            <div className="space-y-4">
              {/* Upload Area */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Image size={18} className="text-indigo-600" />
                    Upload Image
                  </h3>
                </div>
                <div className="p-4">
                  {!imagePreview ? (
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                        dragOver
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                      }`}
                    >
                      <Upload size={40} className="mx-auto mb-3 text-gray-400" />
                      <p className="text-sm text-gray-600 mb-1">
                        Drag & drop an image, click to browse, or paste from clipboard
                      </p>
                      <p className="text-xs text-gray-400">PNG, JPG, WebP, GIF, BMP — up to 20MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFile(file);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full rounded-lg border border-gray-200 max-h-80 object-contain bg-gray-50"
                      />
                      <button
                        onClick={reset}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        title="Remove image"
                      >
                        <X size={14} />
                      </button>
                      <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                        <FileText size={12} />
                        {fileName} ({imageMime})
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Settings */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles size={18} className="text-indigo-600" />
                    Settings
                  </h3>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['plain', 'markdown', 'structured'] as OutputFormat[]).map((fmt) => (
                        <button
                          key={fmt}
                          onClick={() => setOutputFormat(fmt)}
                          className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                            outputFormat === fmt
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          {fmt === 'plain' ? 'Plain Text' : fmt === 'markdown' ? 'Markdown' : 'Structured JSON'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Language (optional)</label>
                    <input
                      type="text"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      placeholder="e.g., Hindi, Marathi, English"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
                    />
                  </div>
                  <button
                    onClick={runOcr}
                    disabled={!imageBase64 || loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Extracting Text...
                      </>
                    ) : (
                      <>
                        <ScanText size={18} />
                        Extract Text
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Right: Result */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm flex flex-col">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileText size={18} className="text-indigo-600" />
                  Extracted Text
                </h3>
                {result && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { copyText(result.text); toast.success('Text copied'); }}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      {copied ? <Check size={12} /> : <Copy size={12} />}
                      Copy
                    </button>
                    <button
                      onClick={downloadText}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      <Download size={12} />
                      Download
                    </button>
                    <button
                      onClick={reset}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      <RotateCcw size={12} />
                      Reset
                    </button>
                  </div>
                )}
              </div>
              <div className="flex-1 p-4 min-h-[400px]">
                {loading ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <Loader2 size={32} className="animate-spin mb-3" />
                    <p className="text-sm">Analyzing document & extracting text...</p>
                    <p className="text-xs mt-1 text-gray-300">Pass 1: Document analysis → Pass 2: Smart extraction</p>
                  </div>
                ) : result ? (
                  <div className="space-y-3">
                    {result.analysis && (
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
                          🧠 {result.strategy}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                          📄 {result.analysis.documentType}
                        </span>
                        {result.analysis.isHandwritten && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-100">
                            ✍️ Handwritten
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                          ⚡ {result.analysis.complexity}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                          🎯 {Math.round(result.analysis.confidence * 100)}%
                        </span>
                      </div>
                    )}
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono bg-gray-50 rounded-lg p-4 max-h-[500px] overflow-auto border border-gray-100">
                      {result.text}
                    </pre>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400 pt-2 border-t border-gray-100">
                      <span>Model: {result.model}</span>
                      <span>Tokens: {result.tokens.total}</span>
                      <span>Format: {result.outputFormat}</span>
                      <span>Time: {result.processingTimeMs}ms</span>
                    </div>

                    {/* Legal Analysis Button */}
                    <button
                      onClick={runAnalysis}
                      disabled={analyzing}
                      className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 transition-all"
                    >
                      {analyzing ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Analyzing Legal Significance...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} />
                          🧠 Analyze Legal Significance
                        </>
                      )}
                    </button>

                    {/* Document Info */}
                    {docInfo && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="px-2 py-1 text-xs font-bold bg-amber-100 text-amber-800 rounded-full uppercase">
                            {docInfo.type?.replace(/_/g, ' ')}
                          </span>
                          {docInfo.jurisdiction && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                              {docInfo.jurisdiction}
                            </span>
                          )}
                          {docInfo.confidence > 0 && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                              {Math.round(docInfo.confidence * 100)}% confidence
                            </span>
                          )}
                        </div>
                        {docInfo.summary && <p className="text-xs text-gray-700">{docInfo.summary}</p>}
                        {docInfo.parties && docInfo.parties.length > 0 && (
                          <p className="text-xs text-gray-600 mt-1"><strong>Parties:</strong> {docInfo.parties.join(', ')}</p>
                        )}
                        {docInfo.sections && docInfo.sections.length > 0 && (
                          <p className="text-xs text-gray-600 mt-1"><strong>Sections:</strong> {docInfo.sections.join(', ')}</p>
                        )}
                      </div>
                    )}

                    {/* Streaming Legal Analysis */}
                    {(legalAnalysis || analyzing) && (
                      <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg max-h-[400px] overflow-auto">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                          🧠 Legal Intelligence Report
                          {analyzing && <Loader2 size={14} className="animate-spin text-amber-500" />}
                        </h4>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {legalAnalysis || 'Analyzing...'}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <ScanText size={40} className="mb-3 opacity-40" />
                    <p className="text-sm">Upload an image and click "Extract Text"</p>
                    <p className="text-xs mt-1">Supports documents, screenshots, handwritten notes, legal papers</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OcrWidgetPage;
